<template>
  <Layout>
    <template #header-container>
      <PageHeader :title="title" :sub-title="subTitle" isSticky>
        <template #afterTitle>
          <div class="d-flex align-items-center gap-2">
            <McTransitionStatus
              :isCreating="isAdding"
              :entityData="showResponse"
              :disabled="hasChanged"
              :statusTransitions="statusTransitions"
              @transition="handleTransitionStatus"
            />

            <b-button
              v-if="isShowSubmitAccurate"
              variant="outline-primary"
              :disabled="disabledSubmitAccurate || isSubmitting"
              @click="modalBranch = true"
            >
              {{ $t('costManagement.sendToProvider', { provider: 'Accurate' }) }}
              <i>
                <img
                  class="icon-provider ms-1"
                  :src="require('@/assets/images/setting/integration/accurate.png')"
                />
              </i>
            </b-button>

            <McButtonBack />
          </div>
        </template>

        <template #banner>
          <div v-if="warningMessage" class="mc-assignment-banner">
            <i class="mc-assignment-banner__icon icon-outline_alert-triangle"></i>
            <div class="mc-assignment-banner__text">
              {{ warningMessage }}
            </div>
          </div>
        </template>
      </PageHeader>
    </template>

    <div class="mc-assignment-form">
      <b-skeleton-wrapper :loading="isLoading">
        <template #loading>
          <b-skeleton-img no-aspect height="72px" class="mc-wizard-skeleton" />
        </template>

        <div class="custom-mc-wizard">
          <McWizard :items="wizardItems" :activeKey="currentStatus" class="mc-wizard" />
        </div>
      </b-skeleton-wrapper>

      <FormulateForm ref="formRef" v-model="formValues" class="mc-form" :errors="formErrors">
        <div class="mc-header__assignment-info d-flex">
          <h4 class="card-title flex-grow-1">Detail</h4>

          <McButton
            variant="secondary"
            class="mx-2"
            :text="$t('global.action.cancel')"
            icon="icon-outline_close"
            :disabled="!hasChanged"
            @click="$refs['mc-modal-cancel'].show()"
          />

          <b-modal
            id="mc-modal-cancel"
            ref="mc-modal-cancel"
            class="mc-modal"
            size="md"
            centered
            scrollable
            :title="$t('global.action.cancel')"
          >
            <div class="card-body p-2">
              <p>
                {{ $t('global.text.modal.cancelChange') }}
                <strong>{{ $t('costManagement.info', { type: costType }) }}</strong
                >?
              </p>
            </div>

            <template #modal-footer="{ cancel }">
              <div class="d-flex mx-0">
                <McButton
                  variant="secondary"
                  class="me-2"
                  :text="$t('global.action.no')"
                  @click="cancel()"
                />

                <McButton variant="primary" :text="$t('global.action.yes')" @click="handleCancel" />
              </div>
            </template>
          </b-modal>

          <McButton
            ref="save-button-ref"
            variant="primary"
            type="submit"
            icon="icon-outline_save"
            :text="$t('global.save')"
            :disabled="!hasChanged && !saveAsDefault"
            @click="handleSubmit"
          />

          <b-modal
            id="mc-modal-update"
            ref="mc-modal-update"
            class="mc-modal"
            size="md"
            centered
            scrollable
            :title="$t('global.action.save')"
          >
            <div class="card-body p-2">
              <p>
                {{ $t('global.text.modal.saveChange') }}
                <strong>{{ $t('costManagement.info', { type: costType }) }}</strong
                >?
              </p>
            </div>

            <template #modal-footer="{ cancel }">
              <div class="d-flex mx-0">
                <McButton
                  variant="secondary"
                  class="me-2"
                  :text="$t('global.action.no')"
                  @click="cancel()"
                />
                <McButton variant="primary" :text="$t('global.action.yes')" @click="submitUpdate" />
              </div>
            </template>
          </b-modal>
        </div>

        <b-card class="mc-assignment-info__card">
          <b-row class="g-4">
            <b-col sm="6">
              <b-row class="g-4">
                <b-col v-for="field in leftSideFields" :key="field.name" sm="12" md="6">
                  <FormulateInput
                    v-bind="field"
                    :ref="`${field.name}-ref`"
                    class="mc-assignment-info__input"
                    validation-behavior="live"
                    :fullObject.sync="costObj[field.name]"
                    :disabled="disableMainForm(field.name)"
                    :error="field.name === 'funds_account_source' ? fundsAccountSourceErr : null"
                  >
                    <template v-if="field.name === 'fleet_task_id'" #label>
                      <div class="d-flex justify-content-between align-items-center">
                        <label>{{ $t(field.label) }} </label>
                        <a
                          v-b-tooltip.topright
                          :title="$t('costManagement.fields.fleetTaskNumberTooltip')"
                        >
                          <i class="icon-outline_info"></i>
                        </a>
                      </div>
                    </template>

                    <template v-if="field.name === 'funds_account_source'">
                      <b-form-group class="mt-1 d-flex flex-column">
                        <input
                          v-model="saveAsDefault"
                          class="form-check-input me-2 custom-control-input"
                          type="checkbox"
                        />

                        <label>{{ $t('global.action.saveAsDefault') }}</label>

                        <div v-if="hasFinancialIntegrationAccess" class="mt-3">
                          <McToggle
                            v-model="includeLicensePlate"
                            :label="$t('costManagement.form.includeLicensePlate')"
                            placement="left"
                          />
                        </div>
                      </b-form-group>
                    </template>
                  </FormulateInput>

                  <template v-if="field.name === 'funds_account_source'">
                    <b-form-group class="mt-1">
                      <input
                        v-model="saveAsDefault"
                        class="form-check-input me-2 custom-control-input"
                        type="checkbox"
                      />

                      <label>{{ $t('global.action.saveAsDefault') }}</label>
                    </b-form-group>
                  </template>
                </b-col>
              </b-row>
            </b-col>

            <b-col v-if="rightSideFields.length > 0" sm="6" class="have-vertical-line">
              <b-row class="g-4">
                <b-col v-for="field in rightSideFields" :key="field.name" sm="12" lg="6">
                  <FormulateInput
                    v-bind="field"
                    :ref="`${field.name}-ref`"
                    class="mc-assignment-info__input"
                    validation-behavior="live"
                    :fullObject.sync="costObj[field.name]"
                    :disabled="disableMainForm(field.name)"
                    :error="field.name === 'funds_account_source' ? fundsAccountSourceErr : null"
                  >
                    <template v-if="field.name === 'fleet_task_id'" #label>
                      <div class="d-flex justify-content-between align-items-center">
                        <label>{{ $t(field.label) }} </label>

                        <a
                          v-b-tooltip.topright
                          :title="$t('costManagement.fields.fleetTaskNumberTooltip')"
                        >
                          <i class="icon-outline_info"></i>
                        </a>
                      </div>
                    </template>
                  </FormulateInput>

                  <template v-if="field.name === 'funds_account_source'">
                    <b-form-group class="mt-1 d-flex flex-column">
                      <input
                        v-model="saveAsDefault"
                        class="form-check-input me-2 custom-control-input"
                        type="checkbox"
                      />

                      <label>{{ $t('global.action.saveAsDefault') }}</label>

                      <div v-if="hasFinancialIntegrationAccess" class="mt-3">
                        <McToggle
                          v-model="includeLicensePlate"
                          :label="$t('costManagement.form.includeLicensePlate')"
                          placement="left"
                        />
                      </div>
                    </b-form-group>
                  </template>
                </b-col>
              </b-row>
            </b-col>
          </b-row>
        </b-card>
      </FormulateForm>

      <CostTable
        ref="cost-table"
        :apiClient="costApiClient"
        :status="currentStatus"
        :assignErrors="assignErrors"
        :invalidFields="invalidCostFields"
      />

      <ModalBranchList
        v-if="modalBranch"
        :value.sync="modalBranch"
        @submit="doSubmit"
        @cancel="doCancel"
      />
    </div>
  </Layout>
</template>

<script>
import moment from 'moment'
import { isEqual, isEmpty } from 'lodash'
import Layout from '@/router/layouts/main'
import PageHeader from '@/components/page-header-v2'
import appConfig from '@/app.config'
import McButtonBack from '@/components/McButtonBack'
import McButton from '@/components/McButton'
import McWizard from '@/components/McWizard'
import McToggle from '@/components/McToggle'
import maskFormValues from '@/components/formulate/TextInput/lib/maskFormValues'
import McTransitionStatus from '@/components/McTransitionStatus'
import ModalBranchList from '../invoice/components/Branch/ModalBranchList'
import { MixPanel } from '@/mixin/mixpanel'
import CostTable from './components/CostTable/index.vue'
import statusTransitions from './lib/constants/status-transitions'
import { fleetTaskFieldsMixin, FLEET_TASK_FIELD_NAME } from './lib/fields/fleetTaskCostFields'
import { vehicleFieldsMixin } from './lib/fields/vehicleCostFields'
import { otherFieldsMixin } from './lib/fields/otherCostFields'
import { errorMessage } from '@/helpers/errorHandler'
import errorMessageList from './lib/constants/error-message-mapper'
import * as FleetTaskCost from '@/services/rest-api/lib/cost-management/fleet-task-cost'
import * as CostItem from '@/services/rest-api/lib/cost-management/cost-item'
import * as Transition from '@/services/rest-api/lib/cost-management/transition'
import * as Integration from '@/services/rest-api/lib/delivery/integration'
import * as Vehicle from '@/services/rest-api/lib/fleet-task/vehicle'
import * as Driver from '@/services/rest-api/lib/fleet-task/driver'
import { Helper } from '@/mixin/helper'
import { mapGetters } from 'vuex'

export default {
  page: {
    title: 'Create Cost Management',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
    McButtonBack,
    McButton,
    McWizard,
    McToggle,
    CostTable,
    McTransitionStatus,
    ModalBranchList,
  },
  mixins: [
    MixPanel,
    Helper,
    statusTransitions,
    fleetTaskFieldsMixin,
    vehicleFieldsMixin,
    otherFieldsMixin,
  ],
  data() {
    return {
      isLoading: false,
      costObj: {},
      currentStatus: '',
      formValues: {},
      formErrors: {},
      fleetTaskCostData: {},
      showResponse: {},
      invalidFormValues: {},
      invalidCostFields: [],
      errorResponse: {},
      costNumber: '',
      isAlreadySubmitted: false,
      integrationProvider: [],
      isDeletedByAccurate: false,
      warningMessage: '',
      modalBranch: false,
      isSubmitting: false,
      fundsAccountSourceErr: null,
      saveAsDefault: false,
      defaultValue: null,
      includeLicensePlate: true,
      isSettingFormFromApi: false,
    }
  },
  computed: {
    ...mapGetters({
      hasAccessAll: 'identity/hasAccessAll',
      getFeatureFlagValue: 'identity/getFeatureFlagValue',
    }),

    costTypeCode() {
      const currentRoute = this.$route.path

      if (currentRoute.includes('travel-cost')) return 'FLEET_TASK'
      else if (currentRoute.includes('vehicle')) return 'VEHICLE'
      else return 'OTHER'
    },

    costType() {
      if (this.costTypeCode === 'FLEET_TASK') return this.$t('costManagement.type.travelCost')
      else if (this.costTypeCode === 'VEHICLE') return this.$t('costManagement.type.vehicleCost')
      else return this.$t('costManagement.type.otherCost')
    },

    formFields() {
      if (this.costTypeCode === 'VEHICLE') return this.vehicleFields
      else if (this.costTypeCode === 'FLEET_TASK') return this.fleetTaskFields
      else return this.otherFields
    },

    wizardItems() {
      return [
        { key: 'Draf', label: this.$t('costManagement.status.draft') },
        { key: 'Dikonfirmasi', label: this.$t('costManagement.status.confirmed') },
      ]
    },

    title() {
      return this.$route.params.id
        ? `${this.$t('costManagement.form.detailTitle', { type: this.costType })} #${
            this.costNumber
          }`
        : `${this.$t('costManagement.form.title', { type: this.costType })}`
    },

    subTitle() {
      switch (this.costTypeCode) {
        case 'FLEET_TASK':
          return this.$t('costManagement.form.subtitle.travelCost')
        case 'VEHICLE':
          return this.$t('costManagement.form.subtitle.vehicleCost')
        case 'OTHER':
          return this.$t('costManagement.form.subtitle.otherCost')
        default:
          return '-'
      }
    },

    leftSideFields() {
      return this.formFields.filter((field) => {
        const hide = field.hide ? !field.hide() : true
        if (this.costTypeCode === 'OTHER') return hide

        const blackListLabel = ['funds_account_source']

        return hide && !blackListLabel.includes(field.name)
      })
    },

    rightSideFields() {
      if (this.costTypeCode === 'OTHER') return []
      return this.formFields.filter((field) => {
        const hide = field.hide ? !field.hide() : true
        const whiteListLabel = ['funds_account_source']

        return hide && whiteListLabel.includes(field.name)
      })
    },

    isAdding() {
      return this.$route.params.id === undefined
    },

    hasChanged() {
      if (this.isLoading) return false

      let currentFormValues = this.transformFields(this.formValues, this.formFields)
      let oldFormValues = {}

      if (!this.isAdding) oldFormValues = { ...this.fleetTaskCostData }

      const res = this.filterUnchangedData(currentFormValues, oldFormValues)
      return res[1]
    },

    costApiClient() {
      if (!this.$route.params.id) return
      return {
        get: FleetTaskCost.getItems,
        show: CostItem.show,
        update: CostItem.update,
        store: CostItem.store(this.$route.params.id),
        delete: CostItem.deleteSingle(this.$route.params.id),
      }
    },

    invalidFieldKeys() {
      return Object.keys(this.invalidFormValues)
    },

    isShowSubmitAccurate() {
      const hasFleetTaskReference =
        !!this.formValues.fleet_task_id ||
        !!this.showResponse.reference_id ||
        !!this.costObj[FLEET_TASK_FIELD_NAME]?.id

      return (
        this.currentStatus === 'Dikonfirmasi' &&
        this.hasFinancialIntegrationAccess &&
        this.integrationProvider.includes('ACCURATE') &&
        this.costTypeCode === 'FLEET_TASK' &&
        hasFleetTaskReference
      )
    },

    hasFinancialIntegrationAccess() {
      return this.hasAccessAll([
        'financial-cost-integration-accurate:create',
        'financial-cost-integration-accurate:read',
        'financial-cost-integration-accurate:update',
        'financial-cost-integration-accurate:delete',
      ])
    },

    hasTmsAccurateScopes() {
      return this.hasAccessAll([
        'tms-integration-accurate:delete',
        'tms-integration-accurate:update',
        'tms-integration-accurate:read',
        'tms-integration-accurate:create',
      ])
    },

    disabledSubmitAccurate() {
      if (!this.isAlreadySubmitted) return false
      if (this.isDeletedByAccurate) return false
      return true
    },

    costTypeAnalytic() {
      if (this.costTypeCode === 'FLEET_TASK') return 'Biaya Perjalanan'
      else if (this.costTypeCode === 'VEHICLE') return 'Biaya Kendaraan'
      else return 'Biaya Lainnya'
    },
  },
  watch: {
    async formValues() {
      if (this.invalidFieldKeys.length === 0) return

      this.invalidFieldKeys.forEach((fieldErrorKey) => {
        if (this.invalidFormValues[fieldErrorKey] != this.formValues[fieldErrorKey]) {
          // delete the field from the state to show errors
          this.$delete(this.formErrors, fieldErrorKey)
          this.$delete(this.invalidFormValues, fieldErrorKey)
        }
      })

      // empty the errorResponse if all error key is deleted
      if (this.invalidFieldKeys.length === 0) {
        this.errorResponse = {}
        this.invalidFormValues = {}
      }
    },

    'formValues.funds_account_source': async function (newVal) {
      if (newVal) this.fundsAccountSourceErr = null
    },

    [`formValues.${FLEET_TASK_FIELD_NAME}`]: async function (newVal) {
      if (this.isSettingFormFromApi) return
      if (newVal) {
        const driverId = this.costObj[FLEET_TASK_FIELD_NAME].driver_user.id
        const vehicleId = this.costObj[FLEET_TASK_FIELD_NAME].vehicle.id
        await this.setVehicleAndDriverFullObj(driverId, vehicleId)
        this.$set(this.formValues, 'driver_id', driverId)
        this.$set(this.formValues, 'vehicle_id', vehicleId)
      }
    },

    [`formValues.vehicle_id`]: async function (newVal) {
      if (this.isSettingFormFromApi) return
      if (this.costTypeCode !== 'FLEET_TASK') return

      // if there is selected fleet task, dont set the driver from vehicle obj
      if (this.formValues.fleet_task_id) return

      const driverId = this.costObj.vehicle_id?.driver?.id ?? undefined

      if (newVal) {
        await this.setVehicleAndDriverFullObj(driverId, undefined)
        this.$set(this.formValues, 'driver_id', driverId)
        this.$set(this.costObj, 'driver_id', this.costObj.vehicle_id.driver)
      }
    },
  },

  async mounted() {
    const { value } = await FleetTaskCost.getDefaultValue()
    if (value) {
      this.defaultValue = value
      if (this.isAdding) this.$set(this.formValues, 'funds_account_source', value)
    }

    if (!this.$route.params.id) return

    this.showData()
    this.hasTmsAccurateScopes && this.setDeliveryIntegrationProvider()

    if (!this.hasTmsAccurateScopes || !(this.costTypeCode === 'FLEET_TASK')) return

    const { errors } = await FleetTaskCost.compare(this.$route.params.id)

    if (typeof errors === 'object' && 'scope' in errors) return

    if (errors && typeof errors === 'object') {
      const errorMessage = Helper.methods.withErrorHandler({ errors }, errorMessageList)

      if ('integration' in errors) {
        this.$mcToast.error(errorMessage)
        return
      }

      if ('external_transaction' in errors && errors.external_transaction[0] === 'not found') {
        this.isDeletedByAccurate = true
      }

      this.warningMessage = errorMessage
    }
  },
  methods: {
    async handleCancel() {
      this.formValues = {}
      this.costObj = {}
      this.resetErrors()
      this.saveAsDefault = false

      if (!this.isAdding) {
        const data = this.showResponse
        this.$set(this.costObj, FLEET_TASK_FIELD_NAME, {
          id: data.reference_id,
          driver_user: data.driver,
          vehicle: {
            ...data.vehicle,
          },
        })

        this.formValues = { ...this.fleetTaskCostData }
      } else {
        this.$set(this.formValues, 'funds_account_source', this.defaultValue)
      }

      await this.assignErrors(this.errorResponse)

      this.$refs['mc-modal-cancel'].hide()
    },

    resetErrors() {
      this.formErrors = {}
      this.errorResponse = {}
      this.invalidFormValues = {}
    },

    async assignErrors(errors) {
      if (Object.keys(errors).length === 0) return

      const fieldErrorKeys = Object.keys(errors)

      this.resetErrors()
      this.invalidCostFields = []

      await this.$nextTick()

      // assign the error field to loadstable, form errors and duplicate the form values with field that error
      fieldErrorKeys.forEach((fieldErrorKey) => {
        if (fieldErrorKey === 'transaction_items') {
          this.invalidCostFields = [...errors[fieldErrorKey]]
          return
        }

        // assign the proper key to show the error message
        this.$set(this.invalidFormValues, fieldErrorKey, this.formValues[fieldErrorKey])
        this.$set(
          this.formErrors,
          fieldErrorKey,
          this.$t(
            errorMessageList[fieldErrorKey]?.[errors[fieldErrorKey][0]] ??
              this.$t('global.inputFormatError')
          )
        )
      })

      await this.$nextTick()
      // assign the errors to a state
      this.errorResponse = errors

      // assign the errors in LoadsTable
      this.$refs['cost-table'].AssignInvalidErrors()

      this.$mcToast.error(this.$t('global.inputFormatError'))
    },

    handleSubmit() {
      if (this.isSubmitting) return this.$mcToast.warning('Mohon tunggu')

      if (this.$refs.formRef.hasErrors || this.invalidFieldKeys.length) {
        this.$mcToast.error(this.$t('global.inputFormatError'))
        return
      }

      if (this.isAdding) {
        this.submitStore()
      } else {
        this.$refs['mc-modal-update'].show()
      }
    },

    async submitUpdate() {
      this.isSubmitting = true

      this.formValues = this.transformFields(this.formValues, this.formFields)

      const driverObjIsEmpty =
        !this.costObj['driver_id'] || Object.keys(this.costObj['driver_id']).length === 0

      const vehicleObjIsEmpty =
        !this.costObj['vehicle_id'] || Object.keys(this.costObj['vehicle_id']).length === 0

      const fleetTaskObjIsEmpty =
        !this.costObj['fleet_task_id'] || Object.keys(this.costObj['fleet_task_id']).length === 0

      const fleetTaskIsUpdated =
        this.showResponse.reference_id != this.costObj[FLEET_TASK_FIELD_NAME]?.id

      const nullFleetTask = fleetTaskObjIsEmpty || this.costObj[FLEET_TASK_FIELD_NAME]?.id === null

      const payload = {
        reference_type: this.costTypeCode,
        transaction_date:
          this.showResponse.transaction_date.split('T')[0] != this.formValues.transaction_date
            ? this.formValues.transaction_date
            : undefined,
        type: 'OUTCOME',
        funds_account_source:
          this.showResponse.funds_account_source != this.formValues.funds_account_source
            ? this.formValues.funds_account_source
            : undefined,
      }

      if (this.costTypeCode === 'FLEET_TASK') {
        payload['fleet_task_id'] = fleetTaskIsUpdated
          ? this.costObj[FLEET_TASK_FIELD_NAME]?.id
          : undefined

        if (payload.fleet_task_id && payload.fleet_task_id?.length) {
          payload.fleet_task_type = this.costObj[FLEET_TASK_FIELD_NAME]?.fleet_task_type
        }

        payload['driver_id'] =
          this.showResponse.driver.id !== this.costObj['driver_id']?.id
            ? driverObjIsEmpty
              ? -1
              : this.costObj['driver_id']?.id
            : undefined

        payload['vehicle_id'] =
          this.showResponse.vehicle.id !== this.costObj['vehicle_id']?.id
            ? vehicleObjIsEmpty
              ? -1
              : this.costObj['vehicle_id']?.id
            : undefined

        if (fleetTaskIsUpdated && nullFleetTask) {
          payload['fleet_task_id'] = ''
          payload['driver_id'] = driverObjIsEmpty ? -1 : this.costObj['driver_id']?.id
          payload['vehicle_id'] = vehicleObjIsEmpty ? -1 : this.costObj['vehicle_id']?.id
        }
      } else if (this.costTypeCode === 'VEHICLE') {
        payload['vehicle_id'] =
          this.showResponse.vehicle.id !== this.costObj['vehicle_id']?.id
            ? vehicleObjIsEmpty
              ? -1
              : this.costObj['vehicle_id']?.id
            : undefined
      }

      const { errors, message } = await FleetTaskCost.update(this.$route.params.id, payload)
      if (errors) {
        this.$mcToast.error(errorMessage(errorMessageList[message]))
        this.isSubmitting = false
        this.$refs['mc-modal-update'].hide()
        return
      }

      this.doSubmitDefaultValue()

      this.$mcToast.success(this.$t('global.updateSuccess'))
      await this.$nextTick()
      await this.showData()

      this.isSubmitting = false
      this.$refs['mc-modal-update'].hide()
    },

    async submitStore() {
      this.isSubmitting = true
      this.formValues = this.transformFields(this.formValues, this.formFields)

      const payload = {
        reference_type: this.costTypeCode,
        fleet_task_id: this.costObj[FLEET_TASK_FIELD_NAME]?.id,
        fleet_task_type: this.costObj[FLEET_TASK_FIELD_NAME]?.fleet_task_type,
        transaction_date: this.formValues.transaction_date,
        type: 'OUTCOME',
        funds_account_source: this.formValues.funds_account_source,
        vehicle_id: this.costObj['vehicle_id']?.id,
        driver_id: this.costObj['driver_id']?.id,
      }

      const { errors, message, data } = await FleetTaskCost.store(payload)

      if (errors) {
        if (this.isObject(errors)) {
          await this.assignErrors(errors)
        } else {
          this.$mcToast.error(errorMessage(errorMessageList[message]))
        }

        this.isSubmitting = false
        return
      }

      this.doSubmitDefaultValue()

      this.track('Create Cost', { 'Created From': 'Web', Type: this.costTypeAnalytic })

      this.$mcToast.success({
        component: 'McToastDynamicBody',
        props: {
          contents: [
            { tag: 'strong', text: data.name, class: '__blue-highlight-text' },
            { tag: 'span', text: 'berhasil disimpan sebagai' },
            { tag: 'strong', text: 'Draf', class: '__status' },
          ],
        },
      })

      if (this.costTypeCode === 'FLEET_TASK') {
        this.$router.replace({ name: 'Detail Cost Management', params: data })
      } else if (this.costTypeCode === 'VEHICLE') {
        this.$router.replace({ name: 'Detail Vehicle Cost Management', params: data })
      } else {
        this.$router.replace({ name: 'Detail Other Cost Management', params: data })
      }

      await this.showData(data.id)

      this.isSubmitting = false
    },

    async doSubmitDefaultValue() {
      if (this.saveAsDefault && this.formValues.funds_account_source !== this.defaultValue) {
        const res = await FleetTaskCost.saveAsDefault({
          value: this.formValues.funds_account_source,
        })

        if (res?.errors) {
          this.$mcToast.error(errorMessage('costManagement.transaction.failedSaveValueAsDefault'))
        }

        this.defaultValue = this.formValues.funds_account_source
        this.saveAsDefault = false
      }
    },

    async showData(id = null) {
      this.isSettingFormFromApi = true
      this.isLoading = true

      const { errors, data } = await FleetTaskCost.show(id ?? this.$route.params.id)

      data.status = { name: data.status }
      this.isAlreadySubmitted = !!data?.external_reference?.id
      this.showResponse = data
      this.costNumber = data.number
      this.currentStatus = data.status.name[0] + data.status.name.slice(1).toLowerCase()

      this.costObj = {
        ...this.costObj,
        [FLEET_TASK_FIELD_NAME]:
          data.fleet_task.name !== null
            ? {
                id: data.reference_id,
                name: data.fleet_task.name,
                fleet_task_type: data.fleet_task.fleet_task_type,
                driver_user: data.driver,
                vehicle: {
                  license_plate: data.vehicle.name,
                },
              }
            : null,
        vehicle_id:
          data.vehicle.id !== null
            ? {
                id: data.vehicle.id,
                license_plate: data.vehicle.name,
              }
            : null,
        driver_id:
          data.driver.id !== null
            ? {
                id: data.driver.id,
                name: data.driver.name,
              }
            : null,
      }

      const parsedData = this.parseData(data)
      const unmaskedData = maskFormValues(parsedData, this.formFields)

      this.formValues = unmaskedData
      this.fleetTaskCostData = { ...unmaskedData }
      this.isLoading = false

      this.$nextTick(() => {
        this.isSettingFormFromApi = false
      })

      // assign the errors
      if (errors) {
        await this.assignErrors(errors)
      }
    },

    parseData(data) {
      const parsedData = {}

      this.formFields.forEach((field) => {
        if (data[field.name] === undefined && data[field.showKey] === undefined) return

        parsedData[field.name] = field.showKey ? data[field.showKey].id : data[field.name]

        switch (field.name) {
          case 'transaction_date':
            if (!data.transaction_date) break
            parsedData[field.name] = moment(parsedData[field.name]).format('YYYY-MM-DD')
            break
          case 'fleet_task_id':
            parsedData[field.name] = field.showKey ? data[field.showKey].name : data[field.name]
            break
          default:
            break
        }
      })

      return parsedData
    },

    filterUnchangedData(newValue, oldValue) {
      const keys = new Set([...Object.keys(newValue), ...Object.keys(oldValue)])
      const _newValue = {}

      keys.forEach((key) => {
        // if both value is falsy
        if (!oldValue[key] && !newValue[key]) return

        // if oldValue has same value with newValue
        if (oldValue[key] == newValue[key]) return

        // if the oldValue is 'Invalid date' and newValue is null
        if (oldValue[key] === 'Invalid date' && newValue[key] === null) return

        // if it is array
        if (Array.isArray(oldValue[key]) || Array.isArray(newValue[key])) {
          // if oldValue is undefined but the newValue is empty array
          if (!oldValue[key] && newValue[key].length === 0) return

          // matching oldValue and newValue array
          const sortedNewValue = newValue[key]?.length ? newValue[key].sort() : newValue[key]
          const sortedOldValue = oldValue[key] ? oldValue[key].sort() : oldValue[key]

          const isSameArray = isEqual(sortedNewValue, sortedOldValue)

          if (isSameArray) return
        }

        _newValue[key] = newValue[key]
      })

      const entries = Object.entries(_newValue)
      const hasChanged = entries.length > 0 && entries.every((value) => value)
      return [_newValue, hasChanged]
    },

    async handleTransitionStatus(data) {
      try {
        if (this.isAdding) return

        if (this.$refs.formRef.hasErrors || this.invalidFieldKeys.length) {
          this.$refs.formRef.showErrors()
          return this.$mcToast.error(this.$t('global.inputFormatError'))
        }

        if (!data.transition.status?.id) {
          throw new Error('Transition status is not defined')
        }

        const { errors, message } = await Transition.transition(this.$route.params.id, {
          status: data.transition.status.id,
          ...data.additionalPayload,
        })

        if (errors) {
          let subMsg = ''
          if (message === 'invalid FO status') {
            subMsg = this.formValues?.fleet_task_id
          }
          return this.$mcToast.error(errorMessage(errorMessageList[message], subMsg))
        }
        // this is errors contain object data
        else if (!isEmpty(errors)) {
          await this.assignErrors(errors)
          return
        }

        this.$mcToast.success({
          component: 'McToastDynamicBody',
          props: {
            contents: [
              { tag: 'strong', text: this.showResponse?.number, class: '__blue-highlight-text' },
              { tag: 'span', text: 'berhasil' },
              { tag: 'strong', text: this.$t(data.transition.status?.name), class: '__status' },
            ],
          },
        })

        this.isDisabled = true
        await this.showData()
        if (this.hasTmsAccurateScopes) {
          await this.setDeliveryIntegrationProvider()
        }
      } catch (e) {
        if (e.response?.data?.errors) {
          await this.assignErrors(e.response.data.errors)
        }
      }
    },

    disableMainForm(inputKey = null) {
      if (inputKey == 'funds_account_source') return false

      if (this.currentStatus === this.status.CONFIRMED.code) {
        return true
      }

      const fleetTaskIsFilled =
        this.formValues[FLEET_TASK_FIELD_NAME] !== undefined &&
        this.formValues[FLEET_TASK_FIELD_NAME] !== null
      if (inputKey === 'vehicle_id') return fleetTaskIsFilled
      if (inputKey === 'driver_id') return fleetTaskIsFilled

      return false
    },

    async setDeliveryIntegrationProvider() {
      const redirectUrl = window.location.origin + window.location.pathname
      const list = await Integration.getUserIntegrationList(redirectUrl)

      for (let i = 0; i < list.providers.length; i++) {
        const provider = list.providers[i]
        if (provider.code === 'ACCURATE' && provider.is_integrated) {
          this.integrationProvider.push(provider.code)
          return
        }
      }

      this.integrationProvider = []
      return
    },

    async doSubmit(branchId = null) {
      // Add validaton funds_account_source
      if (!this.formValues.funds_account_source) {
        this.fundsAccountSourceErr = this.$t('global.validation.required')
        return
      }
      this.isSubmitting = true
      const payload = {
        id: this.showResponse?.id,
        branch_id: branchId,
        is_with_license_plate: this.includeLicensePlate,
      }
      const resp = await FleetTaskCost.submitToAccurate(payload)
      if (!resp) {
        this.isSubmitting = false
        return
      }
      const { errors, message, errors_v2 } = resp

      if (errors && typeof errors === 'object') {
        this.isSubmitting = false
        const errorKeys = ['activity_id', 'activity_chart_of_account']
        const asArray = Object.entries(errors)
        const filtered = asArray.filter(([key, _]) => key !== 'transaction_items')
        const errorsMap = { errors: Object.fromEntries(filtered) }

        if ('transaction_items' in errors) {
          this.assignErrors({ transaction_items: errors.transaction_items })
        }

        if (filtered.length) {
          let errorMessage = Helper.methods.withErrorHandler(
            errorsMap,
            errorMessageList,
            errorKeys,
            true
          )
          if (Array.isArray(errorMessage)) {
            errorMessage.forEach((errorMessageItem) => {
              let errMsg = errorMessageItem
              if (typeof errorMessageItem === 'string') {
                // Special error handling
                let position = errMsg.search('account number')
                const keys = ['activity_chart_of_account', 'funds_account_source']
                keys.forEach((key) => {
                  if (key in errors) {
                    const msgArr = errorMessageItem.split(' ')
                    errMsg =
                      position !== -1
                        ? this.$t(errorMessageList[key]['account number not found'], {
                            number: msgArr[2],
                          })
                        : errMsg

                    if (key === 'funds_account_source') {
                      // mapping error to the field
                      this.fundsAccountSourceErr = errMsg
                    }
                    if (key === 'activity_chart_of_account') {
                      this.$mcToast.error(errMsg)
                    }
                  }
                })

                // mapping error license_plate with dynamic business data name
                const errorKey = 'license_plate'
                if (errorKey in errors) {
                  const target = 'not found in'
                  position = errMsg.search(target)
                  const business = errMsg.slice(position + target.length).trim()
                  errMsg =
                    position !== -1
                      ? this.$t(errorMessageList[errorKey]['not found'], { business })
                      : errMsg
                  this.$mcToast.error(errMsg)
                }
              }
            })
          }

          if (typeof errorMessage === 'string') {
            this.$mcToast.error(errorMessage)
          }
        }

        if (errors_v2) {
          const { errorsMessageHtml, extractedUrl } = Helper.methods.formatAccurateErrorMessages(
            errors_v2.issues
          )

          await this.$mcMessageDialog
            .error({
              title: 'Gagal mengirim biaya perjalanan ke Accurate',
              subtitle: 'Pesan dari Accurate:',
              content: errorsMessageHtml,
              useHtml: true,
              secondaryButton: { text: 'Salin Link Instalasi', visible: !!extractedUrl },
            })
            .then(async (action) => {
              if (action === 'secondary' && extractedUrl) {
                try {
                  await navigator.clipboard.writeText(extractedUrl)
                  this.$mcToast.success('Link instalasi berhasil disalin')
                } catch (copyErr) {
                  console.error('Failed to copy:', copyErr)
                  this.$mcToast.error('Gagal menyalin link instalasi')
                }
              }
            })
        }
        return
      }

      if (errors === true) {
        this.isSubmitting = false
        const errMsg = message
          ? this.$t(errorMessageList[message])
          : Helper.methods.withErrorHandler()
        this.$mcToast.error(errMsg)
        return
      }

      this.$mcToast.success(this.$t('costManagement.successPostToAccurate'))
    },

    doCancel() {
      this.isSubmitting = false
    },

    transformFields(formValues, fields) {
      const formValuesKeys = Object.keys(formValues)
      const validKeys = formValuesKeys.filter((key) => formValues[key] !== false)

      return Object.assign(
        {},
        ...validKeys.map((key) => {
          const currentField = fields.find((item) => item.key == key)
          const value = formValues[key]

          if (currentField && currentField.type === 'text') {
            if (!formValues[key]) return { [key]: '' }
            return { [key]: formValues[key].trim() }
          }

          return { [key]: value }
        })
      )
    },

    async setVehicleAndDriverFullObj(driverId, vehicleId) {
      const calls = []
      if (driverId) calls.push(Driver.show(driverId))
      if (vehicleId) calls.push(Vehicle.show(vehicleId))
      const [driver, vehicle] = await Promise.all(calls)

      if (driver) this.$set(this.costObj, 'driver_id', driver.data.paginated_result[0])
      if (vehicle) this.$set(this.costObj, 'vehicle_id', vehicle.data.paginated_result[0])
    },
  },
}
</script>

<style lang="scss" scoped>
:deep(.mc-assignment-form) {
  .mc-wizard,
  .mc-header__assignment-info,
  .mc-assignment-info__read-only,
  .mc-assignment-info__card {
    display: inline-block;
    margin-top: 0px;
    margin-bottom: 12px;
    width: 100%;
    .mc-label {
      color: $teal-300;
    }
  }

  @include mediaQueryMinWidth('md') {
    .mc-wizard-skeleton {
      max-width: 600px;
    }
  }

  .mc-header__assignment-info,
  .mc-assignment-info__read-only {
    margin-top: 12px;
  }

  .custom-mc-wizard {
    width: 180px;
  }

  .mc-assignment-info__input-group {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 20px;
    margin-top: 12px;
    justify-content: space-between;
  }

  .mc-assignment-info__input {
    width: 100%;
    padding: 0px;
    height: fit-content !important;
  }
}

:deep(.header-container) {
  .icon-provider {
    width: 20px;
  }
}

:deep(.mc-assignment-banner) {
  width: 100%;
  height: 72px;
  background-color: #fff3e6;
  color: #ff8a00;
  font-size: 13px;
  padding: 0px 20px;
  margin-top: -24px;

  display: flex;
  align-items: center;
  gap: 40px;

  .mc-assignment-banner__text {
    height: fit-content;
  }

  .mc-assignment-banner__icon:before {
    color: #ff8a00;
    font-size: 32px;
  }
}

.mc-assignment-banner + .mc-assignment-form {
  margin-top: 60px;
}

.have-vertical-line {
  border-left: 1px solid $mep-color-text-100;
}

@media (max-width: 768px) {
  .have-vertical-line {
    border-left: none;
  }
}
</style>
